<script lang="ts" setup>
import {computed, onMounted, ref} from 'vue';
import {captureException, captureMessage} from "@sentry/browser";
import QuizButton from '@/components/Quiz/QuizButton.vue'
import QuizSecureTag from '@/components/Quiz/QuizSecureTag.vue'
import {useQuizStore} from '@/stores/quizStore';
import {validateEmail} from '@/helpers/validations';
import {createToken} from '@/services/quizService';
import { type IVehicle, vehicleFactory } from '@/types/vehicle'
import {useRoute} from "vue-router";
import {gtmCompleteRegistration, mktProspectQualified} from "@/helpers/marketingEvents.ts";
import UiModal from "@/components/Ui/Modal/UiModal.vue";
import PrivacyPolicyContent from "@/components/Policies/PrivacyPolicyContent.vue";
import SraFeeCap from "@/components/Policies/SraFeeCap.vue";

const {
  vrm,
  vin,
  consentMarketing,
  email: storeEmail,
  isFetching,
  goToNextStep,
  setEmail,
  setToken,
  showAlert,
  setVehicles,
  setIsFetching,
  goToStepPath,
  setConsentMarketing,
  setLeadSource,
} = useQuizStore()

const route = useRoute()

declare global {
  interface Window {
    Cookiebot: any
  }
}

const email = ref(storeEmail.value || '')
const consent = ref(consentMarketing.value)
const modalIsOpen = ref(false)
const showErrors = ref(false)
const privacyPolicymodalIsOpen = ref(false)

const isValid = computed(() => !!email.value && validateEmail(email.value))

function handleSubmit() {
  if (!isValid.value) {
    showErrors.value = true
    return;
  }

  const rawOid = route.query?.oid
  const oid = (Array.isArray(rawOid) ? rawOid[0] : rawOid) as (string | undefined)

  setIsFetching(true)
  createToken({
    email: email.value.trim(),
    vrm: vrm.value,
    vin: vin.value,
    offer_id: oid,
    consent_marketing: consent.value,
    cookiebot_consent_marketing: !!window['Cookiebot'] ? window['Cookiebot']?.consent?.marketing : null,
  })
    .then((response) => {
      const vehicles = response.vehicles
        .filter((v: IVehicle) => v.dpf_qualified)
        .map(vehicleFactory)
      setConsentMarketing(consent.value)
      setEmail(email.value)
      setToken(response.token)
      setIsFetching(false)
      setVehicles(vehicles)

      const leadSource = response?.campaign?.lead_source_name || 'Website'
      setLeadSource(leadSource)
      gtmCompleteRegistration()
      mktProspectQualified(leadSource, consent.value)

      if (!vehicles.length) {
        goToStepPath(5)
        return
      }
      goToNextStep()
    })
    .catch(async (err) => {
      const response = await err.response?.json()
      if (response?.error) {
        showAlert(response.error)
      } else {
        captureMessage("Error on createToken: EmailAndAgreement.vue");
        captureException(err);
      }
    })
    .finally(() => {
      setIsFetching(false)
    })
}

onMounted(() => {
  setVehicles([])
})
</script>

<template>
  <h2 class="text-2xl font-bold mb-4">Pop in your email and we'll do the heavy lifting</h2>

  <p class="text-gray-600 mb-4 font-medium">We'll keep you posted on what you might be owed, think
    of it as your claim's co-pilot, guiding you to the finish line</p>

  <form @submit.prevent="handleSubmit">
    <div class="mb-4">
      <label for="email" class="block text-gray-500 mb-2">Email</label>
      <input v-model="email" type="email" id="email" placeholder="email@example.com"
             class="w-full p-3 border border-gray-300 rounded-lg focus:outline-none"
             :class=" {
             'border-red-500': showErrors && !isValid
           }">
    </div>

    <div class="flex items-start mb-2">
      <div class="flex items-start">
        <input v-model="consent" type="checkbox" id="consent"
               class="h-4 w-4 text-blue-600 border-gray-300 rounded">
      </div>
      <label for="consent" class="ml-3 text-gray-600 text-xs">
        By ticking this box you agree to opt-in to marketing for other relevant services (opt-out
        available by emailing <a href="mailto:privacyofficer@johnsonlawgroup.com"
                                 class="italic hover:underline">privacyofficer@johnsonlawgroup.com</a>).

      </label>
    </div>

    <p class="text-gray-600 text-xs mb-4">
      By clicking continue, you authorise Claims For Cars to assess your claim and contact you via
      phone, text, and email. You further acknowledge that you have received in writing the SRA Fee
      Cap Description for financial mis-selling claims provided on this website and <a href="#"
                                                                                       class="italic hover:underline"
                                                                                       @click.prevent="modalIsOpen = true">here</a>.
      You
      further agree to our <a href="#" class="italic hover:underline"
                              @click.prevent="privacyPolicymodalIsOpen = true">privacy policy</a>.
    </p>

    <QuizButton label="Continue" :disabled="isFetching" :isFetching="isFetching" id="email-and-agreement-button" />
  </form>

  <QuizSecureTag class="mt-6"/>

  <UiModal title="How does the SRA Fee Cap apply to my claim?" :is-open="modalIsOpen" class="bg-white"
           @close="modalIsOpen = false">
    <SraFeeCap/>
  </UiModal>


  <UiModal title="Privacy Policy" :is-open="privacyPolicymodalIsOpen" class="bg-white"
           @close="privacyPolicymodalIsOpen = false">
    <PrivacyPolicyContent/>
  </UiModal>
</template>
